/* eslint-disable camelcase */
import React from 'react';
import { Link } from 'react-router-dom';
import Tooltip from '@material-ui/core/Tooltip';
import { FormControl, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { css, StyleSheet } from 'aphrodite';

import { ApiHelper } from '../../../common/helpers/ApiHelper';
import { Helper } from '../../../common/helpers/Helper';
import { UiHelper } from '../../../common/helpers/UiHelper';
import { AppConfig } from '../../../AppConfig';
import { AppStyles, AppTheme } from '../../../styles/AppTheme';
import { CommonTheme } from '../../../styles/Styles';
import { Controller } from '../../../common/Controller';
import { BaseEntityPageSlixta } from '../../../components/BaseEntityPageSlixta';
import { SearchBar } from '../../../common/components/SearchBar';
import { CRMHelper } from '../../../helpers/CRMHelper';

const FILTER_ALL = { key: 'all', label: 'All' }
const STATE_FILTER_OPTIONS = [
  FILTER_ALL,
  { key: 'LIVE', label: 'LIVE' },
  { key: 'DISABLED', label: 'DISABLED' },
]
const CATEGORY_FILTER_OPTIONS = [
  FILTER_ALL,
  { key: 'MARKETING', label: 'MARKETING' },
  { key: 'CRM', label: 'CRM' },
]
const HEALTH_FILTER_OPTIONS = [
  FILTER_ALL,
  { key: 'HEALTHY', label: 'HEALTHY' },
  { key: 'RECOVERY', label: 'RECOVERY' },
  { key: 'DISCONNECTED', label: 'DISCONNECTED' },
]

function filterKey(option) {
  return option && option.key && option.key !== 'all' ? option.key : ''
}

export class CrmInboxes extends BaseEntityPageSlixta {

  constructor(props, options) {
    super(props);
    this.ms = AppConfig.CDP_MS.ENDPOINT
    this.pageTitle = options.pageTitle || Helper.getString('crm-inboxes')
    this.orgId = this.props.match.params.orgId || 'PRODUCT'
    this.staffUserId = Controller.get().getUserId()
    this.breadCrumbs = [
      { title: Helper.getString('home'), to: this.baseRoute() },
      { title: Helper.getString('crm-inboxes') },
    ]
    this.toFetch = [
      { ms: this.ms, method: 'GET', paths: ['crminboxs'], queryParams: { orgId: this.orgId, orgIdUserId: `${this.orgId}-${this.staffUserId}`, pb: 'fieldDef', limit: 100, includeShared: 'true' } },
    ]
    this.apiData = {
      orgId: this.orgId,
      userId: this.staffUserId,
    }
    this.pageKey = 'crm-inboxes'
    this.tableHeadersMap = {
      Index: (current) => current.__index + 1,
      Inbox: (current) => {
        const health = CRMHelper.getInboxHealthIndicator(current)
        return (
          <div className={css(Styles.inboxCell)}>
            <div className={css(Styles.inboxPrimaryRow)}>
              <Tooltip title={`${health.tooltip} (click for history)`} placement='top'>
                <span
                  className={css(Styles.healthDot, Styles.healthDotClickable)}
                  style={{ backgroundColor: health.color }}
                  onClick={(e) => {
                    e.stopPropagation()
                    CRMHelper.openInboxHealthHistory({
                      orgId: this.orgId,
                      inboxId: current.id,
                      email: current.email,
                    })
                  }}
                  role='button'
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      e.stopPropagation()
                      CRMHelper.openInboxHealthHistory({
                        orgId: this.orgId,
                        inboxId: current.id,
                        email: current.email,
                      })
                    }
                  }}
                />
              </Tooltip>
              <span>{current.email}</span>
              {current.emailProvider && <span className={css(Styles.line2)}> [{current.emailProvider}]</span>}
            </div>
            {current.senderName && <span className={css(Styles.line2)}>{current.senderName}</span>}
          </div>
        )
      },
      State: (current) => (
        <span className={current.state === 'LIVE'
          ? css(Styles.line2, Styles.statusBadge, Styles.statusBadgeLive)
          : css(Styles.line2, Styles.statusBadge)}>
          {current.state || '—'}
        </span>
      ),
      Category: (current) => (
        <div className={css(Styles.badgeRow)}>
          {CRMHelper.getInboxCategories(current).map((cat) => (
            <span key={cat} className={css(Styles.line2, Styles.categoryBadge)}>{cat}</span>
          ))}
        </div>
      ),
    }
    this.tableHeaders = Object.keys(this.tableHeadersMap)
    this.uploadLocation = `${this.orgId}/__FILENAME__`
    this.uploadMs = AppConfig.CDP_MS.ENDPOINT
    this.headerActions = [
      {
        label: 'Add new',
        onClick: () => this.openModalForAdd(),
      },
      {
        label: 'Refresh',
        tooltip: 'Refresh',
        icon: 'refresh',
        type: 'icon-label',
        onClick: () => this.fetchItems(),
      },
    ]
    this.state = {
      ...this.state,
      searchText: '',
      stateFilterSelected: FILTER_ALL,
      categoryFilterSelected: FILTER_ALL,
      healthFilterSelected: FILTER_ALL,
    }
  }

  getListingFilters(overrides = {}) {
    const state = { ...this.state, ...overrides }
    return {
      searchText: state.searchText || '',
      state: filterKey(state.stateFilterSelected),
      category: filterKey(state.categoryFilterSelected),
      healthState: filterKey(state.healthFilterSelected),
    }
  }

  applyListingFilters(overrides = {}) {
    const filtered = CRMHelper.applyInboxListingFilters(this.crmInboxes, this.getListingFilters(overrides))
    this.setState({
      ...overrides,
      items: filtered,
    })
  }

  onStateFilterChange = (e, val) => {
    if (!val || !val.key) return
    this.applyListingFilters({ stateFilterSelected: val })
  }

  onCategoryFilterChange = (e, val) => {
    if (!val || !val.key) return
    this.applyListingFilters({ categoryFilterSelected: val })
  }

  onHealthFilterChange = (e, val) => {
    if (!val || !val.key) return
    this.applyListingFilters({ healthFilterSelected: val })
  }

  onSearch = (searchText = '') => {
    this.applyListingFilters({ searchText })
  }

  clearSearch = () => {
    this.applyListingFilters({ searchText: '' })
  }

  onFetchSuccess(results) {
    this.crmInboxes = results[0].items || []
    this.fieldDef = this.formDefinition = CRMHelper.applyFetchPolicyFieldLabels(results[0].fieldDef)
    const filtered = CRMHelper.applyInboxListingFilters(this.crmInboxes, this.getListingFilters())
    this.setState({ items: filtered })
  }

  onAdd() {
    this.setState({
      apiMethod: 'POST',
      apiEndPoint: ApiHelper.makeUrlPath2({ ms: this.ms, paths: ['crminboxs'], queryParams: { orgId: this.orgId } }),
    })
  }

  onEdit(current) {
    this.setState({
      apiMethod: 'PUT',
      apiEndPoint: ApiHelper.makeUrlPath2({ ms: this.ms, paths: ['crminboxs', current.id], queryParams: { orgId: this.orgId } }),
    })
  }

  onDelete(current) {
    this.setState({
      apiMethod: 'DELETE',
      apiEndPoint: ApiHelper.makeUrlPath2({ ms: this.ms, paths: ['crminboxs', current.id], queryParams: { orgId: this.orgId } }),
    })
  }

  onActions = (current) => {
    const actions = []
    actions.push(UiHelper.buttonInfo(this, current))
    actions.push(UiHelper.buttonEdit(this, current))
    if (Helper.isShowInternal()) {
      actions.push(<Tooltip title={'Resync inbox with crm'} placement='top-start' key={current.id + 'resync'}>
        <span className={css(AppStyles.entityTableLinkIcon)}>
          <i className='material-icons-outlined' onClick={() => this.openModalForResync(current)}>sync</i>
        </span>
      </Tooltip>)
      actions.push(<Tooltip title={'Force send next sequence email'} placement='top-start' key={current.id + 'force-send'}>
        <span className={css(AppStyles.entityTableLinkIcon)}>
          <i className='material-icons-outlined' onClick={() => this.openModalForForceSend(current)}>send</i>
        </span>
      </Tooltip>)
      actions.push(<Tooltip title={'Verify'} placement='top-start' key={current.id + 'verify'}>
        <span className={css(AppStyles.entityTableLinkIcon)}>
          <i className='material-icons-outlined' onClick={() => this.openModalForVerify(current)}>checklist</i>
        </span>
      </Tooltip>)
    }
    actions.push(
      <Link to={`${this.baseRoute()}/crm-inboxes/${current.id}`} key={current.id + 'detail'}>
        <Tooltip title='View inbox' placement='top-start'>
          <span className={css(AppStyles.entityTableLinkIcon)}>
            <i className='material-icons-outlined'>arrow_forward_ios</i>
          </span>
        </Tooltip>
      </Link>
    )
    //actions.push(UiHelper.buttonDelete(this, current))
    return actions
  }

  getInboxInfoFieldDef(current) {
    const field = (key, label, placement) => ({
      key,
      type: 'edit_text',
      label,
      placement,
      v_required: { value: false },
    })
    return {
      steps: [{
        fields: [
          field('email', 'Email', 'left'),
          field('senderName', 'Sender name', 'right'),
          field('emailProvider', 'Email provider', 'left'),
          field('state', 'State', 'right'),
          field('categoryDisplay', 'Category', 'left'),
          field('healthDisplay', 'Health', 'right'),
          field('fetchPolicyDisplay', 'Fetch policy', 'left'),
          field('managedHealth', 'Deliverability Optimised', 'right'),
          field('continuousWarmup', 'Continuous Warmup', 'left'),
          field('smtpConnectionState', 'SMTP connection', 'right'),
          field('imapConnectionState', 'IMAP connection', 'left'),
          field('sharedInbox', 'Shared inbox', 'right'),
        ]
      }],
      formType: 'info',
      title: current.email || 'Inbox info',
    }
  }

  openModalForInfo(current) {
    const health = CRMHelper.getInboxHealthIndicator(current)
    const infoCurrent = {
      ...current,
      categoryDisplay: CRMHelper.getInboxCategories(current).join(', '),
      healthDisplay: health.tooltip,
      fetchPolicyDisplay: CRMHelper.getFetchPolicyLabel(current.fetchPolicy) || '—',
      managedHealth: current.managedHealth || '—',
      continuousWarmup: current.continuousWarmup || '—',
      smtpConnectionState: current.smtpConnectionState || 'CONNECTED',
      imapConnectionState: current.imapConnectionState || 'CONNECTED',
      emailProvider: current.emailProvider || '—',
      sharedInbox: current.sharedInbox || 'NO',
      senderName: current.senderName || '—',
      state: current.state || '—',
    }
    super.openModalForInfo(infoCurrent, this.getInboxInfoFieldDef(current))
  }

  openModalForEdit(current) {
    const fieldDef = CRMHelper.applyFetchPolicyFieldLabels(JSON.parse(JSON.stringify(this.fieldDef)))
    const canEditSharedInbox = Controller.get().userMgr().isProductSU()
      || Controller.get().roleHelper().hasCurrentOrgRole('SUPERUSER')
    if(fieldDef && fieldDef.steps && fieldDef.steps.length > 0 && fieldDef.steps[0].fields) {
      fieldDef.steps[0].fields.forEach((field)=>{
        if(current && current.sharedInbox === 'YES' && current.userId !== this.staffUserId && !canEditSharedInbox && !['senderName','signatureRichText', 'signatureImage'].includes(field.key)) {
          field.disabled = true
        }
      })
    }
    super.openModalForEdit(current, fieldDef)
  }

  openModalForResync(current) {
    this.formDefinition = {
      steps: [{
        fields: [
          {
            key: 'inboxId',
            type: 'radio',
            label: 'Email Inbox',
            options: [{key: current.id, label: current.email}],
            v_required: { value: true, err: 'Provide email inbox' },
            placement: 'left',
            //defaultValue: current.id,
          },
          {
            key: 'syncStartTime',
            type: 'dateTime',
            label: 'Resync From',
            hint: 'Provide Resync Start Time',
            tip: 'The date and time from which the inbox should be resynced',
            v_required: { value: false },
            placement: 'right'
          },
        ]
      }],
      disableAlwaysFormFields: ['inboxId'],
      formType: 'edit',
      title: 'Resync',
    }
    Helper.populateFormDefinitionValues(this.formDefinition, {inboxId: current.id})
    this.setState({
      apiMethod: 'POST',
      apiEndPoint: ApiHelper.makeUrlPath2({
        ms: AppConfig.CDP_MS.ENDPOINT,
        paths: ['miscs', 'resync-inbox'],
        queryParams: { orgId: this.orgId, inboxId: current.id },
      }),
      modalOpen: true,
    })
  }

  openModalForForceSend(current) {
    this.formDefinition = {
      steps: [{
        fields: [
          {
            key: 'inboxId',
            type: 'radio',
            label: 'Email Inbox',
            options: [{key: current.id, label: current.email}],
            v_required: { value: true, err: 'Provide email inbox' },
            placement: 'left',
            //defaultValue: current.id,
          },
        ]
      }],
      disableAlwaysFormFields: ['inboxId'],
      formType: 'edit',
      title: 'Force Send Next Sequence Email',
    }
    Helper.populateFormDefinitionValues(this.formDefinition, {inboxId: current.id})
    this.setState({
      apiMethod: 'POST',
      apiEndPoint: ApiHelper.makeUrlPath2({
        ms: AppConfig.CDP_MS.ENDPOINT,
        paths: ['miscs', 'force-send-next-drip'],
        queryParams: { orgId: this.orgId, inboxId: current.id },
      }),
      modalOpen: true,
    })
  }

  openModalForVerify(current) {
    this.formDefinition = {
      steps: [{
        fields: [
          {
            key: 'inboxId',
            type: 'radio',
            label: 'Email Inbox',
            options: [{key: current.id, label: current.email}],
            v_required: { value: true, err: 'Provide email inbox' },
            placement: 'left',
            //defaultValue: current.id,
          },
        ]
      }],
      disableAlwaysFormFields: ['inboxId'],
      formType: 'edit',
      title: 'Verify',
      ctaButtonText: 'Verify',
    }
    Helper.populateFormDefinitionValues(this.formDefinition, {inboxId: current.id})
    this.setState({
      apiMethod: 'POST',
      apiEndPoint: ApiHelper.makeUrlPath2({
        ms: AppConfig.CDP_MS.ENDPOINT,
        paths: ['miscs', 'verify-inbox'],
        queryParams: { orgId: this.orgId, inboxId: current.id },
      }),
      modalOpen: true,
    })
  }

  renderFilterAutocomplete(label, value, options, onChange) {
    return (
      <div className={css(Styles.formControlContainer)}>
        <p className={css(Styles.label)}>{label}</p>
        <FormControl className={css(Styles.formControlMainFilters)} variant='outlined'>
          <Autocomplete
            value={value}
            options={options}
            onChange={onChange}
            getOptionLabel={option => option.label || option.key || ''}
            getOptionSelected={(option, v) => option.key === v.key}
            disableClearable
            renderInput={params => (
              <TextField
                {...params}
                variant='standard'
                className={css(Styles.formControlFilters)}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  readOnly: true,
                  classes: {
                    root: css([Styles.textInputStyles, Styles.autoCompleteInputRoot]),
                    input: css(Styles.formInputStyle, Styles.textInput),
                  },
                }}
              />
            )}
          />
        </FormControl>
      </div>
    )
  }

  renderAboveTable() {
    return (
      <div className={css(Styles.toolbar)}>
        <div className={css(Styles.filterContainer)}>
          {this.renderFilterAutocomplete(
            'State',
            this.state.stateFilterSelected || FILTER_ALL,
            STATE_FILTER_OPTIONS,
            this.onStateFilterChange
          )}
          {this.renderFilterAutocomplete(
            'Category',
            this.state.categoryFilterSelected || FILTER_ALL,
            CATEGORY_FILTER_OPTIONS,
            this.onCategoryFilterChange
          )}
          {this.renderFilterAutocomplete(
            'Health',
            this.state.healthFilterSelected || FILTER_ALL,
            HEALTH_FILTER_OPTIONS,
            this.onHealthFilterChange
          )}
        </div>
        <div className={css(Styles.searchBarContainer)} onSubmit={e => e.preventDefault()}>
          <SearchBar
            hint='Search by email or sender name'
            onSearch={this.onSearch}
            onClearSearch={this.clearSearch}
            searchText={this.state.searchText}
            searchButtonLabel='Filter'
            clearSearchLabel='Clear'
            autoRefresh
            debounceDuration={1}
            autoFocus={false}
            hideFiltersContainer
          />
        </div>
      </div>
    )
  }

  onTableValue(current, index) {
    if (!this.tableHeaders[index]) { return '' }
    return this.tableHeadersMap[this.tableHeaders[index]](current)
  }
}

const Styles = StyleSheet.create({
  toolbar: {
    width: '100%',
    marginBottom: 16,
  },
  filterContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 16,
  },
  formControlContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 5,
    flex: '0 0 auto',
    width: 200,
    maxWidth: 200,
  },
  label: {
    fontSize: 14,
    color: CommonTheme.subTextColor,
    margin: 0,
    display: 'inline-block',
  },
  formControlMainFilters: {
    width: '100%',
    maxWidth: 200,
  },
  formControlFilters: {
    borderRadius: AppTheme.borderRadius,
    border: '1px solid #E5E5E5',
    overflow: 'hidden',
  },
  textInputStyles: {
    padding: '11px 20px',
    flex: 1,
    textAlign: 'left',
    color: '#222222',
    fontSize: 16,
  },
  autoCompleteInputRoot: {
    paddingRight: 48,
  },
  formInputStyle: {
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  textInput: {
    padding: '4px 0px',
  },
  searchBarContainer: {
    width: '100%',
    minWidth: 0,
    overflowX: 'hidden',
  },
  line2: {
    fontSize: 12,
    color: 'grey'
  },
  inboxCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
  },
  inboxPrimaryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  healthDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
    display: 'inline-block',
  },
  healthDotClickable: {
    cursor: 'pointer',
  },
  badgeRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    alignItems: 'flex-start',
  },
  statusBadge: {
    backgroundColor: AppTheme.primaryBg,
    padding: 5,
    borderRadius: 10,
    wordBreak: 'keep-all',
    display: 'block',
    textAlign: 'center',
    minWidth: 100,
    maxWidth: 100,
  },
  statusBadgeLive: {
    backgroundColor: '#3BDE86',
    color: '#222222'
  },
  categoryBadge: {
    backgroundColor: AppTheme.primaryBg,
    padding: '3px 8px',
    borderRadius: 8,
    display: 'inline-block',
  },
})
